/**
 * render_baked_demo_video — manual trigger for the baked-demo-video render
 * pipeline (#3850, follow-up to #3837 / #3839).
 *
 * Wraps `POST /api/jobs/:jobId/baked-demo-video`. The endpoint is idempotent
 * server-side, so two consecutive calls on a queued/running/succeeded job
 * just surface the existing state — no double enqueue.
 *
 * `wait` defaults to `true` because agents calling this tool typically need
 * the resulting `videoUrl` inline; fire-and-forget is the unusual case and
 * available via `wait: false`.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { BakedDemoVideoStatusResponse } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';

export interface RenderBakedDemoVideoArgs {
  jobId: string;
  wait?: boolean;
  timeoutSeconds?: number;
}

export const renderBakedDemoVideoToolDefinition: Tool = {
  name: 'render_baked_demo_video',
  description: `Render an enhanced video (logo bookends, burned-in captions,
key-moment overlays) for a completed QA test job. Use this when you've decided
a finished job is worth promoting into a polished, shareable artifact and the
caller didn't opt into enhanced video at job creation.

The render is idempotent: if a render already exists for the job
(queued/running/succeeded), the existing state is returned and no duplicate
work is enqueued. A render whose latest attempt failed is automatically
retried on the next call.

Default behavior is to wait until the render reaches a terminal state, which
typically takes 1–5 minutes depending on the recording length. Pass
\`wait: false\` to enqueue and return immediately; you can poll the status
later with another call (which will short-circuit on the existing row).

Returns the rendered video URL when succeeded, or a precise reason
(\`no_recording\` / \`no_transcript\`) when the worker pipeline can't run.`,
  inputSchema: {
    type: 'object',
    properties: {
      jobId: {
        type: 'string',
        description: 'ID of a completed job to render an enhanced video for.',
      },
      wait: {
        type: 'boolean',
        description: 'Block until the render reaches a terminal state (succeeded/failed). Default: true.',
        default: true,
      },
      timeoutSeconds: {
        type: 'number',
        description: 'Maximum seconds to wait when wait=true. Default: 600 (10 minutes).',
        minimum: 30,
        maximum: 3600,
      },
    },
    required: ['jobId'],
  },
};

const POLL_INTERVAL_MS = 10_000;
const DEFAULT_TIMEOUT_SECONDS = 600;

function formatStatusBlock(jobId: string, status: BakedDemoVideoStatusResponse): string {
  const lines: string[] = [
    `Job ID: ${jobId}`,
    `Status: ${status.status ?? 'no render yet'}`,
    `Attempts: ${status.attempts}`,
  ];
  if (status.lastError) lines.push(`Last error: ${status.lastError}`);
  if (status.videoUrl) lines.push(`Video URL: ${status.videoUrl}`);
  if (status.publicToken && !status.publicTokenDisabled) {
    lines.push(`Public share path: /api/public/baked-demo-video/${jobId}/${status.publicToken}`);
  }
  if (status.createdAt) lines.push(`Created: ${status.createdAt}`);
  if (status.finishedAt) lines.push(`Finished: ${status.finishedAt}`);
  return lines.join('\n');
}

function describeReason(reason: string | undefined): string | null {
  if (reason === 'no_recording') {
    return 'Cannot render enhanced video: this job has no LiveKit recording (the worker pipeline needs the raw recording).';
  }
  if (reason === 'no_transcript') {
    return 'Cannot render enhanced video: this job has no transcript (captions are driven from per-word timestamps).';
  }
  return null;
}

export async function handleRenderBakedDemoVideo(
  args: RenderBakedDemoVideoArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { jobId } = args;
  const wait = args.wait !== false; // default true
  const timeoutSeconds = args.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS;

  if (!jobId) {
    return {
      content: [{ type: 'text', text: 'jobId is required' }],
      isError: true,
    };
  }

  let status: BakedDemoVideoStatusResponse;
  try {
    status = await jobClient.triggerBakedDemoVideoRender(jobId);
  } catch (error) {
    const err = error as Error & { status?: number; reason?: string };
    if (err.status === 422) {
      const message = describeReason(err.reason) ?? `Render unavailable: ${err.reason ?? err.message}`;
      return { content: [{ type: 'text', text: message }], isError: true };
    }
    if (err.status === 403) {
      return { content: [{ type: 'text', text: err.message }], isError: true };
    }
    return {
      content: [{
        type: 'text',
        text: `Failed to enqueue baked-demo-video render

Job ID: ${jobId}
Error: ${err.message}
${err.status ? `HTTP status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }

  // Already terminal — no waiting needed regardless of `wait` flag.
  if (status.status === 'succeeded' || status.status === 'failed') {
    return finalize(jobId, status);
  }

  if (!wait) {
    return {
      content: [{
        type: 'text',
        text: `Enhanced-video render enqueued.

${formatStatusBlock(jobId, status)}

Call render_baked_demo_video again with the same jobId to check progress, or
omit \`wait: false\` to block until the render finishes.`,
      }],
    };
  }

  // Poll until terminal or timeout.
  const startTime = Date.now();
  const timeoutMs = timeoutSeconds * 1000;

  while (true) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const elapsed = Date.now() - startTime;
    if (elapsed >= timeoutMs) {
      const latest = await jobClient.getBakedDemoVideoStatus(jobId).catch(() => status);
      return {
        content: [{
          type: 'text',
          text: `Timeout waiting for baked-demo-video render

${formatStatusBlock(jobId, latest)}

Elapsed: ${Math.floor(elapsed / 1000)}s

The render is still in progress. Call render_baked_demo_video again with the
same jobId to keep polling, or pass a higher timeoutSeconds.`,
        }],
        isError: true,
      };
    }

    const latest = await jobClient.getBakedDemoVideoStatus(jobId);
    if (latest.status === 'succeeded' || latest.status === 'failed') {
      return finalize(jobId, latest);
    }
  }
}

function finalize(
  jobId: string,
  status: BakedDemoVideoStatusResponse,
): { content: Array<{ type: 'text'; text: string }>; isError?: boolean } {
  if (status.status === 'failed') {
    return {
      content: [{
        type: 'text',
        text: `Enhanced-video render failed

${formatStatusBlock(jobId, status)}`,
      }],
      isError: true,
    };
  }
  return {
    content: [{
      type: 'text',
      text: `Enhanced-video render ${status.status === 'succeeded' ? 'succeeded' : 'in progress'}

${formatStatusBlock(jobId, status)}`,
    }],
  };
}
